import React, { useState, useEffect } from "react";
import WeekSelector from "../weekSelector";
import { Button, Modal, Table, Spinner, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const AdminReports = () => {
  const { t } = useTranslation();

  const [selectedWeek, setSelectedWeek] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_BASE = "https://godrive.com.pl/api";

  const fetchReports = async () => {
    if (!selectedWeek) return;
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/reports-by-week?week_number=${selectedWeek}`);
      const data = await res.json();
      setReports(data);
    } catch (err) {
      console.error("❌ Помилка отримання рапортів:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWeek) fetchReports();
  }, [selectedWeek]);

  const publishOne = async (id) => {
    await fetch(`${API_BASE}/publish-report/${id}`, { method: "PUT" });
    fetchReports();
  };

  const unpublishOne = async (id) => {
    await fetch(`${API_BASE}/unpublish-report/${id}`, { method: "PUT" });
    fetchReports();
  };

  const publishAll = async () => {
    if (!selectedWeek) return;
    await fetch(`${API_BASE}/publish-reports-batch`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ week_number: selectedWeek })
    });
    fetchReports();
  };

  const openModal = (report) => {
    setSelectedReport(report);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedReport(null);
    setShowModal(false);
  };

  const columnNames = {
    id: "ID",
    tips: t("tips"),
    bonus: t("bonus"),
    appfee: t("fee"),
    appfeetaxes: `${t("fee")} ${t("taxes").toLowerCase()}`,
    inccash: t("cash"),
    incnetto: t("netProfit"),
    incbrutto: t("brutto"),
    incbruttoapp: `${t("brutto")} ${t("app")}`,
    incnettocash: `${t("netProfit")} ${t("cash")}`,
    incbruttocash: `${t("brutto")} ${t("cash")}`,
    created_at: t("created"),
    week_number: t("week"),
    driver_lastname: t("name"),
    driver_firstname: t("surname"),
    other: t("other"),
    carrent: t("carRent"),
    partner: t("partner"),
    refunds: t("refunds"),
    taxeszus: `${t("taxes")} ZUS`,
    returnvat: `${t("return")} VAT`,
    accountant: t("accountant"),
    other_expenses: t("otherCosts")
  };

  const renderTableSection = (title, data) => {
    if (!data || typeof data !== 'object') return null;

    return (
      <div className="mb-4">
        <h5 className="text-primary">{title}</h5>
        {Object.entries(data).map(([sectionKey, sectionValue]) => {
          const normalizedKey = sectionKey.toLowerCase();
          if (normalizedKey === "other_expenses") return null;

          if (normalizedKey === "other") {
            const otherExpenses = Array.isArray(data.other_expenses) ? data.other_expenses : [];

            return (
              <Table bordered responsive size="sm" key={sectionKey} className="mb-2">
                <tbody>
                  <tr>
                    <td style={{ width: "70%" }} className="fw-bold text-capitalize">{columnNames[normalizedKey] || sectionKey}</td>
                    <td className="text-end" style={{ whiteSpace: "pre-line", width: "30%"}}>
                      {parseFloat(sectionValue).toFixed(2)}
                      {otherExpenses.length > 0 &&
                        "\n" +
                        otherExpenses
                          .map(
                            (item) =>
                              `${item.description || ""} (${parseFloat(item.amount).toFixed(2)})`
                          )
                          .join("\n")}
                    </td>
                  </tr>
                </tbody>
              </Table>
            );
          }

          if (typeof sectionValue === 'object' && sectionValue !== null) {
            return (
              <div key={sectionKey} className="mb-2">
                <h6 className="text-secondary">{sectionKey}</h6>
                <Table bordered responsive size="sm">
                  <tbody>
                    {Object.entries(sectionValue).map(([key, value]) => {
                      const normalizedSubKey = key.toLowerCase();
                      if (normalizedSubKey === "other_expenses") return null;
                      return (
                        <tr key={key}>
                          <td style={{ width: "70%" }} className="fw-bold text-capitalize">{columnNames[normalizedSubKey] || key}</td>
                          <td style={{ width: "30%" }} className="text-end">
                            {typeof value === 'number'
                              ? value.toFixed(2)
                              : typeof value === 'string'
                              ? value
                              : typeof value === 'object' && value.amount
                              ? `${parseFloat(value.amount).toFixed(2)} PLN`
                              : JSON.stringify(value)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            );
          }

          return (
            <Table bordered responsive size="sm" key={sectionKey} className="mb-2">
              <tbody>
                <tr>
                  <td style={{ width: "70%" }} className="fw-bold text-capitalize">{columnNames[normalizedKey] || sectionKey}</td>
                  <td style={{ width: "30%" }} className="text-end">{sectionValue}</td>
                </tr>
              </tbody>
            </Table>
          );
        })}
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">📋 {t("raports")} {t("for").toLowerCase()} {t("week").toLowerCase()}</h2>

      <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />

      {loading && <div className="text-center my-4"><Spinner animation="border" /></div>}

      {!loading && reports.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-end mb-2">
            <Button variant="success" onClick={publishAll}>📢 {t("save")} {t("all").toLowerCase()}</Button>
          </div>

          <Table bordered responsive hover className="text-center">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>{t("driver")}</th>
                <th>{t("status")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <tr key={report.id}>
                  <td>{index + 1}</td>
                  <td>{report.firstName} {report.lastName}</td>
                  <td>
                    <span className={`badge bg-${report.status === 'published' ? 'success' : 'secondary'}`}>{report.status}</span>
                  </td>
                  <td>
                    <Button size="sm" variant="info" className="me-2" onClick={() => openModal(report)}>
                      🔍 {t("preview")}
                    </Button>
                    {report.status === 'draft' ? (
                      <Button size="sm" variant="warning" onClick={() => publishOne(report.id)}>✅ {t("save")}</Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => unpublishOne(report.id)}>⬅️ {t("draft")}</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {selectedReport && (
        <Modal show={showModal} onHide={closeModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{t("raport")} {t("for").toLowerCase()} {selectedReport.firstName} {selectedReport.lastName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderTableSection(`${t("service")} Uber`, selectedReport.report_json?.report?.uber?.[0])}
            {renderTableSection(`${t("service")} Bolt`, selectedReport.report_json?.report?.bolt?.[0])}
            {renderTableSection(`${t("service")} FreeNow`, selectedReport.report_json?.report?.freenow?.[0])}
            {renderTableSection(t("extraCosts"), selectedReport.report_json?.extraFunds)}

            <Alert variant="info" className="mt-3">
              🧾 {t("debtsMulti")}: {selectedReport.report_json?.selectedDebts?.length || 0}, {t("total").toLowerCase()}: <strong>{selectedReport.report_json?.totalDebt?.toFixed(2)} PLN</strong>
            </Alert>

            <Alert variant="success" className="mt-2">
              💰 {t("payment")}: <strong>{selectedReport.report_json?.payout} PLN</strong>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>{t("close")}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AdminReports;